/**
 * DashboardRoute (Plan 01-13, Task 3).
 *
 * Mounts the dual-mode dashboard:
 *   • <InteractiveMode />  — always-mounted surface with NavShell + Outlet
 *   • <IdleMode />         — always-mounted overlay with 7 tile slots
 *
 * DASH-06: `/` is authless.  Anyone walking past the kitchen tablet can
 * glance at the weather + open-chore count + calendar peek without
 * needing to unlock a profile first.  The /settings route, by contrast,
 * guards via GET /api/auth/me in `<SettingsRoute />`.
 *
 * `useIdleTimer(wallMode)` installs the 60s inactivity listener that flips
 * `isIdle` back to true (DASH-02) — but ONLY on a device switched into
 * wall-tablet mode.  By default the dashboard opens interactive and stays
 * there.  Tapping anywhere on IdleMode wakes via its own `onPointerDown`
 * handler (DASH-03).
 *
 * Test hooks: in vitest/playwright mode, `src/dev/test-hooks.ts` is
 * imported eagerly so Plan 17 specs can drive transitions via
 * window.__stellIdleForceNow().  Production bundles never see the
 * import because Vite tree-shakes the `import.meta.env.MODE === 'test'`
 * branch to a no-op.
 */
import { useWallMode } from './DashboardStore';
import { IdleMode } from './IdleMode';
import { InteractiveMode } from './InteractiveMode';
import { useIdleTimer } from './useIdleTimer';

// Test-only window hooks (W9).  The module self-guards on
// `import.meta.env.MODE === 'test'` so prod bundles don't include the
// code path.  Eager import (not dynamic) keeps the contract simple and
// avoids a rare race where Playwright queries the hook before the
// dynamic import has resolved.
import '../../dev/test-hooks';

export function DashboardRoute() {
  // The 60s auto-idle timer only runs on a device switched into wall-tablet
  // mode (Settings → Enhet).  Everywhere else the dashboard stays
  // interactive and never fades to the ambient hub.
  const wallMode = useWallMode();
  useIdleTimer(wallMode);
  return (
    <>
      <InteractiveMode />
      <IdleMode />
    </>
  );
}
